// src/features/auth/authWrapper.jsx
import { useEffect, useState } from 'react';
import { isEmpty, isLoaded } from 'react-redux-firebase';
import connectedAuthWrapper from 'redux-auth-wrapper/connectedAuthWrapper';
import firebase from '../../app/config/firebase';
import LoadingComponent from '../../app/layout/LoadingComponent';

/**
 * Auth loading component with auto-recovery.
 * If Firebase auth never resolves (corrupted Firestore/App Check cache),
 * clear ONLY the corruption-prone databases and force a clean reload.
 *
 * IMPORTANT: never delete `firebaseLocalStorageDb` (the auth token store) or
 * clear localStorage here. Auth resolution can legitimately take several
 * seconds on a cold PWA start (App Check + slow network), and wiping the token
 * both signs valid users out and — because the Auth SDK still holds that DB
 * open — leaves it in a blocked/locked state that breaks the next sign-in
 * until storage is cleared manually.
 */
const AUTH_RECOVERY_TIMEOUT_MS = 45000;

const AuthLoadingComponent = () => {
  const [recovering, setRecovering] = useState(false);

  useEffect(() => {
    const timer = setTimeout(async () => {
      // Prevent multiple recovery loops
      if (sessionStorage.getItem('auth_recovery')) {
        sessionStorage.removeItem('auth_recovery');
        return;
      }

      setRecovering(true);

      // If Firebase already has a user in memory, the SDK is just slow to
      // surface it to RRF (likely because IndexedDB is busy from a big report).
      // Don't wipe caches; a plain reload is enough and avoids locking the
      // auth token database.
      const currentUser = firebase?.auth()?.currentUser;
      if (currentUser) {
        sessionStorage.setItem('auth_recovery', '1');
        window.location.reload();
        return;
      }

      if ('indexedDB' in window && typeof indexedDB.databases === 'function') {
        try {
          const dbs = await indexedDB.databases();
          const shouldDelete = (name) => {
            const n = String(name || '');
            // Never touch the auth token DB.
            if (!n || n === 'firebaseLocalStorageDb') return false;
            return (
              n.startsWith('firestore/') ||
              n.startsWith('firebase-') ||
              n === 'workbox-expiration'
            );
          };
          await Promise.all(
            (dbs || [])
              .map((d) => d && d.name)
              .filter(Boolean)
              .filter(shouldDelete)
              .map(
                (name) =>
                  new Promise((resolve) => {
                    const req = indexedDB.deleteDatabase(name);
                    req.onsuccess = () => resolve();
                    req.onerror = () => resolve();
                    req.onblocked = () => resolve();
                  }),
              ),
          );
        } catch (_) {}
      }

      sessionStorage.setItem('auth_recovery', '1');
      window.location.reload();
    }, AUTH_RECOVERY_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, []);

  if (recovering) return null;
  return <LoadingComponent />;
};

export const UserIsAuthenticated = connectedAuthWrapper({
  wrapperDisplayName: 'UserIsAuthenticated',
  authenticatedSelector: ({ firebase: { auth } }) =>
    isLoaded(auth) && !isEmpty(auth),
  authenticatingSelector: ({ firebase: { auth } }) => !isLoaded(auth),
  AuthenticatingComponent: AuthLoadingComponent,
  // CRITICAL: don't redirect; let the gate open the modal
  FailureComponent: () => null,
});
